import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Activity, DollarSign, Gauge, Sparkles, Zap } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/app/PageHeader';
import { StatCard } from '@/components/app/StatCard';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { humanizeError } from '@/lib/apiClient';
import { humanizePercent } from '@/lib/utils';
import { toast } from '@/hooks/useToast';
import { aiApi } from '../api/aiApi';

const aiKeys = {
  all: ['ai'] as const,
  logs: (limit: number) => [...aiKeys.all, 'logs', limit] as const,
  stats: () => [...aiKeys.all, 'stats'] as const,
  prompts: () => [...aiKeys.all, 'prompts'] as const,
  prompt: (key: string) => [...aiKeys.all, 'prompts', key] as const,
};

export function AiHomePage() {
  return (
    <div className="space-y-4">
      <PageHeader
        title="AI operations"
        description="Gateway activity, prompt management, and cost monitoring."
      />
      <Tabs defaultValue="stats">
        <TabsList>
          <TabsTrigger value="stats"><Gauge className="mr-1 h-4 w-4" /> Stats</TabsTrigger>
          <TabsTrigger value="logs"><Activity className="mr-1 h-4 w-4" /> Logs</TabsTrigger>
          <TabsTrigger value="prompts"><Sparkles className="mr-1 h-4 w-4" /> Prompts</TabsTrigger>
        </TabsList>
        <TabsContent value="stats" className="space-y-3"><StatsTab /></TabsContent>
        <TabsContent value="logs" className="space-y-3"><LogsTab /></TabsContent>
        <TabsContent value="prompts" className="space-y-3"><PromptsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function StatsTab() {
  const q = useQuery({ queryKey: aiKeys.stats(), queryFn: () => aiApi.stats(), staleTime: 60_000 });
  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data)
    return <ErrorState title="Stats failed" description={humanizeError(q.error)} />;
  const s = q.data;
  return (
    <div className="space-y-3">
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Requests" value={s.totalRequests.toLocaleString()} icon={Activity} />
        <StatCard label="Cost (USD)" value={`$${s.totalCost.toFixed(4)}`} icon={DollarSign} />
        <StatCard label="Cache hit" value={humanizePercent(s.cacheHitRate)} icon={Zap} />
        <StatCard label="Avg latency" value={`${Math.round(s.averageLatencyMs)}ms`} />
      </section>
      <Card>
        <CardHeader><CardTitle className="text-base">Validation outcomes</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-4 gap-3 text-center text-sm">
          <div><div className="text-2xl font-bold text-emerald-600">{s.validationOutcomes.valid}</div><div className="text-xs text-muted-foreground">Valid</div></div>
          <div><div className="text-2xl font-bold text-amber-600">{s.validationOutcomes.repaired}</div><div className="text-xs text-muted-foreground">Repaired</div></div>
          <div><div className="text-2xl font-bold text-orange-600">{s.validationOutcomes.fallback}</div><div className="text-xs text-muted-foreground">Fallback</div></div>
          <div><div className="text-2xl font-bold text-destructive">{s.validationOutcomes.error}</div><div className="text-xs text-muted-foreground">Error</div></div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">By endpoint</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y text-sm">
            {Object.entries(s.byEndpoint).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between px-4 py-2">
                <code className="text-xs">{k}</code>
                <span className="text-xs text-muted-foreground">
                  {v.count} calls · ${v.cost.toFixed(4)} · {v.cacheHits} cache hits
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function LogsTab() {
  const q = useQuery({ queryKey: aiKeys.logs(100), queryFn: () => aiApi.listLogs({ limit: 100 }), staleTime: 30_000 });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState title="Logs failed" description={humanizeError(q.error)} />;
  const logs = q.data?.logs ?? [];

  return (
    <Card>
      <CardContent className="p-0">
        {logs.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">No logs.</p>
        ) : (
          <ul className="divide-y text-sm">
            {logs.map((l) => (
              <li key={l.logId} className="px-4 py-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate">
                      <code className="text-xs">{l.endpointKey}</code> · {l.model} ·{' '}
                      <span className="text-xs text-muted-foreground">{l.promptKey}@{l.promptVersion}</span>
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {l.userId.slice(0, 8)}… · {l.tokensIn}→{l.tokensOut} tokens · ${l.costUsdEstimate.toFixed(5)} · {l.latencyMs}ms · {l.requestedAt.slice(0, 19).replace('T', ' ')}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {l.cacheHit && <Badge variant="success">cache</Badge>}
                    <Badge
                      variant={
                        l.validationOutcome === 'valid'
                          ? 'success'
                          : l.validationOutcome === 'repaired'
                            ? 'warning'
                            : 'destructive'
                      }
                    >
                      {l.validationOutcome}
                    </Badge>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PromptsTab() {
  const q = useQuery({ queryKey: aiKeys.prompts(), queryFn: () => aiApi.listPrompts(), staleTime: 60_000 });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState title="Prompts failed" description={humanizeError(q.error)} />;
  const prompts = q.data?.prompts ?? [];
  return (
    <Card>
      <CardContent className="p-0">
        <ul className="divide-y text-sm">
          {prompts.map((p) => (
            <li key={p.key} className="flex items-center justify-between px-4 py-3">
              <div>
                <Link to={`/ai/prompts/${encodeURIComponent(p.key)}`} className="font-medium hover:underline">
                  {p.key}
                </Link>
                <div className="text-xs text-muted-foreground">
                  v{p.version} · T={p.temperature} · {p.maxTokens} maxTokens · updated {p.updatedAt.slice(0, 10)} by {p.updatedBy}
                </div>
              </div>
              {p.isActive ? (
                <Badge variant="success">Active</Badge>
              ) : (
                <Badge variant="outline">Disabled</Badge>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function AiPromptDetailPage() {
  const { promptKey = '' } = useParams<{ promptKey: string }>();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: aiKeys.prompt(promptKey),
    queryFn: () => aiApi.getPrompt(promptKey),
    enabled: !!promptKey,
  });

  const [draft, setDraft] = React.useState<{
    systemPrompt: string;
    userPromptTemplate: string;
    temperature: number;
    maxTokens: number;
    isActive: boolean;
    changeNote: string;
  } | null>(null);

  React.useEffect(() => {
    if (q.data?.prompt && !draft) {
      setDraft({
        systemPrompt: q.data.prompt.systemPrompt,
        userPromptTemplate: q.data.prompt.userPromptTemplate,
        temperature: q.data.prompt.temperature,
        maxTokens: q.data.prompt.maxTokens,
        isActive: q.data.prompt.isActive,
        changeNote: '',
      });
    }
  }, [q.data?.prompt, draft]);

  const mut = useMutation({
    mutationFn: () => {
      if (!draft) return Promise.reject(new Error('no draft'));
      return aiApi.updatePrompt(promptKey, { ...draft });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
      toast({ title: 'Prompt updated', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Update failed', description: humanizeError(err), variant: 'destructive' }),
  });

  if (q.isLoading || !draft) return <LoadingState />;
  if (q.isError || !q.data)
    return <ErrorState title="Prompt failed" description={humanizeError(q.error)} />;

  const p = q.data.prompt;
  const isTemperatureValid =
    Number.isFinite(draft.temperature) && draft.temperature >= 0 && draft.temperature <= 2;
  const isMaxTokensValid =
    Number.isInteger(draft.maxTokens) && draft.maxTokens >= 1 && draft.maxTokens <= 16000;
  const isDraftValid = isTemperatureValid && isMaxTokensValid;

  return (
    <div className="space-y-4">
      <Link to="/ai" className="inline-flex text-sm text-muted-foreground hover:text-foreground">
        ← Back to AI
      </Link>
      <PageHeader
        title={`Prompt · ${p.key}`}
        description={`v${p.version} · updated ${p.updatedAt.slice(0, 10)} by ${p.updatedBy}`}
        actions={<Badge variant={p.isActive ? 'success' : 'outline'}>{p.isActive ? 'Active' : 'Disabled'}</Badge>}
      />

      <Card>
        <CardHeader><CardTitle className="text-base">Edit prompt</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="system-prompt">System prompt</Label>
            <Textarea
              id="system-prompt"
              value={draft.systemPrompt}
              onChange={(e) => setDraft({ ...draft, systemPrompt: e.target.value })}
              rows={8}
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-prompt">User prompt template</Label>
            <Textarea
              id="user-prompt"
              value={draft.userPromptTemplate}
              onChange={(e) => setDraft({ ...draft, userPromptTemplate: e.target.value })}
              rows={6}
              className="font-mono text-xs"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="temperature">Temperature (0–2)</Label>
              <Input
                id="temperature"
                type="number"
                step="0.1"
                min={0}
                max={2}
                value={Number.isFinite(draft.temperature) ? draft.temperature : ''}
                onChange={(e) => {
                  const n = Number.parseFloat(e.target.value);
                  setDraft({ ...draft, temperature: Number.isFinite(n) ? n : NaN });
                }}
                aria-invalid={!isTemperatureValid}
              />
              {!isTemperatureValid && (
                <p className="text-xs text-destructive">Must be a number 0–2.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="max-tokens">Max tokens (1–16000)</Label>
              <Input
                id="max-tokens"
                type="number"
                min={1}
                max={16000}
                value={Number.isFinite(draft.maxTokens) ? draft.maxTokens : ''}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value, 10);
                  setDraft({ ...draft, maxTokens: Number.isFinite(n) ? n : NaN });
                }}
                aria-invalid={!isMaxTokensValid}
              />
              {!isMaxTokensValid && (
                <p className="text-xs text-destructive">Must be an integer 1–16000.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="is-active">Active</Label>
              <div className="pt-2">
                <Switch
                  id="is-active"
                  checked={draft.isActive}
                  onCheckedChange={(checked) => setDraft({ ...draft, isActive: checked })}
                />
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="change-note">Change note</Label>
            <Input
              id="change-note"
              value={draft.changeNote}
              onChange={(e) => setDraft({ ...draft, changeNote: e.target.value })}
              placeholder="What changed and why?"
            />
          </div>
          <Separator />
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !isDraftValid}>
            {mut.isPending ? 'Saving…' : 'Save changes (bumps version)'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Version history</CardTitle></CardHeader>
        <CardContent className="p-0">
          {q.data.history.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">No history.</p>
          ) : (
            <ul className="divide-y text-sm">
              {q.data.history.map((h) => (
                <li key={`${h.key}-${h.version}`} className="px-4 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">v{h.version}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {h.createdAt.slice(0, 10)} by {h.createdBy}
                      </span>
                    </div>
                  </div>
                  {h.changeNote && <p className="mt-1 text-xs text-muted-foreground">{h.changeNote}</p>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
